import { PhoneCall, SkipForward, PhoneOff as PhoneOffIcon, Ban, ChevronDown, PhoneOff, Mic, MicOff, Pause, Play, Loader2, Voicemail, PhoneForwarded } from "lucide-react";
import React, { memo, useRef, useCallback, useState, useEffect } from "react";
import { usePipelineStages } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { QuickDispositionButtons } from "@/app/_module/components/QuickDispositionButtons";
import { type VoiceCall } from "@/app/_module/contexts/VoiceDeviceProvider";

import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { LeadStatus, LEAD_STATUS_DISPLAY_NAMES } from "@/app/_module/constants/leadStatus";
import { AudioDeviceSelector } from "@/app/_module/components/AudioDeviceSelector";
import { formatCallTimer } from "@/app/_module/utils/formatters";

// Color mapping for lead statuses - matches get-status-color.ts from leads page
// Using hover:!bg and focus:!bg to override dropdown's default white hover/focus state
// Hover darkens the color slightly for visual feedback
interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

const STATUS_COLORS: Record<LeadStatus, string> = {
  [LeadStatus.NEW_LEAD]: "bg-blue-100 text-blue-800 hover:!bg-blue-200 focus:!bg-blue-100",
  [LeadStatus.APPOINTMENT]: "bg-cyan-600 text-cyan-100 hover:!bg-cyan-700 focus:!bg-cyan-600",
  [LeadStatus.UWR]: "bg-amber-500 text-amber-100 hover:!bg-amber-600 focus:!bg-amber-500",
  [LeadStatus.APPROVED]: "bg-emerald-600 text-emerald-100 hover:!bg-emerald-700 focus:!bg-emerald-600",
  [LeadStatus.NO_SALE]: "bg-red-600 text-red-100 hover:!bg-red-700 focus:!bg-red-600",
  [LeadStatus.BAD_NUMBER]: "bg-red-500 text-red-100 hover:!bg-red-600 focus:!bg-red-500",
  [LeadStatus.DO_NOT_CALL]: "bg-amber-800 text-amber-100 hover:!bg-amber-900 focus:!bg-amber-800",
  [LeadStatus.DISPOSED]: "bg-gray-600 text-gray-100 hover:!bg-gray-700 focus:!bg-gray-600",
  [LeadStatus.DECLINED]: "bg-rose-600 text-rose-100 hover:!bg-rose-700 focus:!bg-rose-600",
  [LeadStatus.LAPSED]: "bg-orange-600 text-orange-100 hover:!bg-orange-700 focus:!bg-orange-600",
};

interface PowerDialerControlsProps {
  isDialing: boolean;
  isPaused: boolean;
  onCall: () => void;
  onSkip: () => void;
  onMarkBadNumber: () => void;
  onMarkDNC: () => void;
  onStatusChange: (status: string) => void;
  /** The agent's own disposition buttons; same as Bad Number (file, next lead). */
  onQuickDisposition?: (status: string) => void;
  isDeviceReady: boolean;
  activeCall: VoiceCall | null;
  onDisconnect: () => void;
  onMute: () => void;
  onUnmute: () => void;
  onHold: (callSid?: string) => void;
  onUnhold: (callSid?: string) => void;
  isMuted: boolean;
  isOnHold: boolean;
  callDuration: number;
  hasMicrophone?: boolean | null;
  isHardwareMuted?: boolean; // Detects if headset mute button is pressed
  microphoneAudioLevel?: number; // 0-100 audio level for visual feedback
  audioInputDevices?: AudioDevice[];
  audioOutputDevices?: AudioDevice[];
  selectedInputDeviceId?: string | null;
  selectedOutputDeviceId?: string | null;
  onAudioInputChange?: (deviceId: string) => void;
  onAudioOutputChange?: (deviceId: string) => void;
  onRefreshAudioDevices?: () => void;
  // Speaker volume control
  speakerVolume?: number;
  onSpeakerVolumeChange?: (volume: number) => void;
  // Audio monitoring
  onStartAudioMonitoring?: () => void;
  onStopAudioMonitoring?: () => void;
  // Manual voicemail drop on the active call (plays the drop + hangs up)
  showDropVoicemail?: boolean;
  showTransfer?: boolean;
  onTransfer?: () => void;
  droppingVoicemail?: boolean;
  onDropVoicemail?: () => void;
}

export const PowerDialerControls = memo(function PowerDialerControls({
  isDialing,
  isPaused,
  onCall,
  onSkip,
  onMarkBadNumber,
  onMarkDNC,
  onStatusChange,
  onQuickDisposition,
  isDeviceReady,
  activeCall,
  onDisconnect,
  onMute,
  onUnmute,
  onHold,
  onUnhold,
  isMuted,
  isOnHold,
  callDuration,
  hasMicrophone = true,
  isHardwareMuted = false,
  microphoneAudioLevel = 0,
  audioInputDevices = [],
  audioOutputDevices = [],
  selectedInputDeviceId,
  selectedOutputDeviceId,
  onAudioInputChange,
  onAudioOutputChange,
  onRefreshAudioDevices,
  speakerVolume = 100,
  onSpeakerVolumeChange,
  onStartAudioMonitoring,
  onStopAudioMonitoring,
  showDropVoicemail = false,
  showTransfer = false,
  onTransfer,
  droppingVoicemail = false,
  onDropVoicemail,
}: PowerDialerControlsProps) {
  // Format call duration
  const formatDuration = (seconds: number) => formatCallTimer(seconds);

  // Spam prevention: cooldown with visual feedback after skip/end clicks
  const [skipCooldown, setSkipCooldown] = useState(false);
  const [endCooldown, setEndCooldown] = useState(false);
  const skipTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const endTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
    };
  }, []);

  const handleSkipWithCooldown = useCallback(() => {
    if (skipCooldown) return;
    setSkipCooldown(true);
    onSkip();
    skipTimerRef.current = setTimeout(() => setSkipCooldown(false), 1500);
  }, [onSkip, skipCooldown]);

  const handleEndWithCooldown = useCallback(() => {
    if (endCooldown) return;
    setEndCooldown(true);
    onDisconnect();
    endTimerRef.current = setTimeout(() => setEndCooldown(false), 1500);
  }, [onDisconnect, endCooldown]);

  // The agent's custom pipelines go under the built-in statuses (same list as their Leads page).
  const { data: pipelineStages } = usePipelineStages();
  const customPipelines = (pipelineStages ?? []).filter((s) => !s.isSystem);

  return (
    <div className="space-y-3">
      {/* Primary Call Controls */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <Button
          className="bg-green-600 hover:bg-green-700"
          disabled={isDialing || isPaused || !isDeviceReady || !!activeCall || hasMicrophone === false}
          onClick={onCall}
          size="lg"
        >
          {!isDeviceReady ? (
            <React.Fragment>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Initializing...
            </React.Fragment>
          ) : isDialing ? (
            <React.Fragment>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Calling...
            </React.Fragment>
          ) : (
            <React.Fragment>
              <PhoneCall className="h-4 w-4 mr-2" />
              Call Now
            </React.Fragment>
          )}
        </Button>

        <Button
          disabled={(isPaused && !activeCall) || skipCooldown}
          onClick={handleSkipWithCooldown}
          size="lg"
          variant={activeCall ? "destructive" : "outline"}
          className={activeCall ? "bg-orange-600 hover:bg-orange-700" : ""}
        >
          {skipCooldown ? (
            <React.Fragment>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Skipping...
            </React.Fragment>
          ) : (
            <React.Fragment>
              <SkipForward className="h-4 w-4 mr-2" />
              {activeCall ? "Skip & End Call" : "Skip Lead"}
            </React.Fragment>
          )}
        </Button>

        <Button
          disabled={isPaused}
          onClick={onMarkBadNumber}
          size="lg"
          variant="destructive"
          className="bg-red-500 hover:bg-red-600"
        >
          <PhoneOffIcon className="h-4 w-4 mr-2" />
          Bad Number
        </Button>

        <Button
          disabled={isPaused}
          onClick={onMarkDNC}
          size="lg"
          variant="destructive"
          className="bg-red-700 hover:bg-red-800"
        >
          <Ban className="h-4 w-4 mr-2" />
          Do Not Call
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              disabled={isPaused}
              size="lg"
              variant="outline"
            >
              Set Disposition
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto p-1">
            {Object.values(LeadStatus).map((status) => (
              <DropdownMenuItem
                key={status}
                onClick={() => onStatusChange(status)}
                className={`font-medium rounded-md mb-1 ${STATUS_COLORS[status]}`}
              >
                {LEAD_STATUS_DISPLAY_NAMES[status]}
              </DropdownMenuItem>
            ))}
            {customPipelines.length > 0 ? (
              <React.Fragment>
                <DropdownMenuSeparator />
                {customPipelines.map((stage) => (
                  <DropdownMenuItem
                    key={stage.id}
                    className="font-medium rounded-md mb-1"
                    onClick={() => onStatusChange(stage.systemValue || stage.name)}
                  >
                    <span className="mr-2 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="truncate">{stage.name}</span>
                  </DropdownMenuItem>
                ))}
              </React.Fragment>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {onQuickDisposition ? (
        <QuickDispositionButtons buttonClassName="min-h-9 text-sm" disabled={isPaused} onPick={onQuickDisposition} />
      ) : null}

      {/* Reassuring message while dialing (before call connects) */}
      {isDialing && !activeCall && (
        <div className="flex items-center justify-center gap-2 py-2 px-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span>Dialing lead — please wait for the call to connect</span>
        </div>
      )}

      {/* Active Call Controls - Only show when there's an active call */}
      {activeCall && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          {/* Hardware Mute Warning - Shows when headset mute button is pressed */}
          {isHardwareMuted && !isMuted && (
            <div className="bg-red-100 border border-red-300 rounded-md p-3 mb-3 flex items-center gap-2">
              <MicOff className="h-5 w-5 text-red-600 animate-pulse" />
              <div>
                <span className="text-sm font-medium text-red-800">
                  Microphone Muted by Headset
                </span>
                <p className="text-xs text-red-600">
                  The client cannot hear you. Check your headset mute button.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-blue-900">
                Call in Progress
              </span>
              <span className="text-sm text-blue-700">
                {formatDuration(callDuration)}
              </span>
              {/* Audio Device Selector */}
              {onAudioInputChange && onAudioOutputChange && onRefreshAudioDevices && (
                <AudioDeviceSelector
                  audioInputDevices={audioInputDevices}
                  audioOutputDevices={audioOutputDevices}
                  selectedInputDeviceId={selectedInputDeviceId ?? null}
                  selectedOutputDeviceId={selectedOutputDeviceId ?? null}
                  onAudioInputChange={onAudioInputChange}
                  onAudioOutputChange={onAudioOutputChange}
                  onRefreshDevices={onRefreshAudioDevices}
                  microphoneAudioLevel={microphoneAudioLevel}
                  isHardwareMuted={isHardwareMuted}
                  isMuted={isMuted}
                  speakerVolume={speakerVolume}
                  onSpeakerVolumeChange={onSpeakerVolumeChange}
                  onStartAudioMonitoring={onStartAudioMonitoring}
                  onStopAudioMonitoring={onStopAudioMonitoring}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Button
              onClick={isMuted ? onUnmute : onMute}
              size="sm"
              variant={isMuted ? "default" : "outline"}
              className={isMuted ? "bg-yellow-600 hover:bg-yellow-700" : ""}
            >
              {isMuted ? (
                <React.Fragment>
                  <MicOff className="h-4 w-4 mr-1" />
                  Unmute
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Mic className="h-4 w-4 mr-1" />
                  Mute
                </React.Fragment>
              )}
            </Button>

            <Button
              onClick={() => isOnHold ? onUnhold() : onHold()}
              size="sm"
              variant={isOnHold ? "default" : "outline"}
              className={isOnHold ? "bg-orange-600 hover:bg-orange-700" : ""}
            >
              {isOnHold ? (
                <React.Fragment>
                  <Play className="h-4 w-4 mr-1" />
                  Resume
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Pause className="h-4 w-4 mr-1" />
                  Hold
                </React.Fragment>
              )}
            </Button>

            {showDropVoicemail && (
              <Button
                onClick={onDropVoicemail}
                size="sm"
                variant="outline"
                className="md:col-span-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                disabled={droppingVoicemail}
                title="Leave your pre-recorded voicemail and end the call"
              >
                {droppingVoicemail ? (
                  <React.Fragment>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Dropping…
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Voicemail className="h-4 w-4 mr-1" />
                    Drop Voicemail
                  </React.Fragment>
                )}
              </Button>
            )}
            {showTransfer && (
              <Button
                onClick={onTransfer}
                size="sm"
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                title="Warm-transfer this call to an agency owner"
              >
                <PhoneForwarded className="h-4 w-4 mr-1" />
                Transfer
              </Button>
            )}
            <Button
              onClick={handleEndWithCooldown}
              size="sm"
              variant="destructive"
              className="md:col-span-2"
              disabled={endCooldown}
            >
              {endCooldown ? (
                <React.Fragment>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Ending...
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <PhoneOff className="h-4 w-4 mr-1" />
                  End Call
                </React.Fragment>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
